import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Icon — thin-line, single-stroke SVG set used across both handoffs.
 *
 * Merges the console icon set (components.jsx) and the Core lucide-ish set
 * (back-office components.jsx). All paths render at 24x24, `currentColor` stroke,
 * 1.6 weight; multi-subpath strings are split on `M` so both single- and
 * multi-path glyphs work. Icons are decorative by default (`aria-hidden`);
 * pass a `title` to expose an accessible name.
 */
export const ICONS = {
    // console set
    chevron: 'M9 6l6 6-6 6',
    check: 'M5 12.5l4.5 4.5L19 7',
    x: 'M6 6l12 12M18 6L6 18',
    alert: 'M12 4l9 16H3L12 4zM12 10v4M12 17.5v.5',
    clock: 'M12 7v5l3.5 2M12 21a9 9 0 100-18 9 9 0 000 18z',
    download: 'M12 4v11m0 0l-4-4m4 4l4-4M5 20h14',
    share: 'M16 6l-4-3-4 3M12 3v12M5 13v6a1 1 0 001 1h12a1 1 0 001-1v-6',
    upload: 'M12 20V9m0 0L8 13m4-4l4 4M5 5h14',
    search: 'M11 19a8 8 0 100-16 8 8 0 000 16zM21 21l-4-4',
    scan: 'M4 8V5a1 1 0 011-1h3M16 4h3a1 1 0 011 1v3M20 16v3a1 1 0 01-1 1h-3M8 20H5a1 1 0 01-1-1v-3M4 12h16',
    file: 'M14 3H7a1 1 0 00-1 1v16a1 1 0 001 1h10a1 1 0 001-1V7l-4-4zM14 3v4h4',
    arrow: 'M5 12h14m0 0l-6-6m6 6l-6 6',
    filter: 'M4 5h16l-6 7v6l-4 2v-8L4 5z',
    plus: 'M12 5v14M5 12h14',
    refresh: 'M4 8a8 8 0 0114-4l2 2M20 16a8 8 0 01-14 4l-2-2M18 4v4h-4M6 20v-4h4',
    link: 'M9 15l6-6M10 7l1-1a4 4 0 015.7 5.7l-1 1M14 17l-1 1A4 4 0 017.3 12.3l1-1',
    dot: 'M12 12h.01',
    eye: 'M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7zM12 15a3 3 0 100-6 3 3 0 000 6z',
    exchange: 'M7 8h13m0 0l-4-4m4 4l-4 4M17 16H4m0 0l4 4m-4-4l4-4',
    send: 'M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z',
    chat: 'M4 5h16a1 1 0 011 1v8a1 1 0 01-1 1H9l-4 4v-4H4a1 1 0 01-1-1V6a1 1 0 011-1z',
    clip: 'M16 7l-6.5 6.5a2.5 2.5 0 003.5 3.5l6-6a4.5 4.5 0 00-6.5-6.5l-7 7a6.5 6.5 0 009 9l5-5',
    briefcase: 'M4 8h16a1 1 0 011 1v9a1 1 0 01-1 1H4a1 1 0 01-1-1V9a1 1 0 011-1zM9 8V6a2 2 0 012-2h2a2 2 0 012 2v2M3 13h18',
    // Core set (lucide-ish)
    grid: 'M3 3h7v7H3zM14 3h7v7h-7zM14 14h7v7h-7zM3 14h7v7H3z',
    scale: 'M12 3v18M7 7l-4 7h8zM17 7l4 7h-8zM5 21h14M8 7h8',
    map: 'M9 4 3 6v14l6-2 6 2 6-2V4l-6 2zM9 4v14M15 6v14',
    branch: 'M6 3v12M18 9a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM6 21a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM15 6h-2a4 4 0 0 0-4 4v4',
    book: 'M4 19.5A2.5 2.5 0 0 1 6.5 17H20M4 19.5A2.5 2.5 0 0 0 6.5 22H20V2H6.5A2.5 2.5 0 0 0 4 4.5z',
    beaker: 'M9 3h6M10 3v6l-5 9a2 2 0 0 0 2 3h10a2 2 0 0 0 2-3l-5-9V3M7 14h10',
    chevR: 'M9 18l6-6-6-6',
    chevD: 'M6 9l6 6 6-6',
    history: 'M3 3v5h5M3.05 13A9 9 0 1 0 6 5.3L3 8M12 7v5l4 2',
    layers: 'M12 2 2 7l10 5 10-5zM2 17l10 5 10-5M2 12l10 5 10-5',
    shield: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z',
};
export function Icon({ name, size = 16, stroke = 1.6, color = 'currentColor', style, title }) {
    const d = ICONS[name];
    const labelled = title !== undefined;
    return (_jsxs("svg", { width: size, height: size, viewBox: "0 0 24 24", fill: "none", stroke: color, strokeWidth: stroke, strokeLinecap: "round", strokeLinejoin: "round", style: { flexShrink: 0, ...style }, role: labelled ? 'img' : undefined, "aria-hidden": labelled ? undefined : true, "aria-label": labelled ? title : undefined, children: [title !== undefined && _jsx("title", { children: title }), d
                .split('M')
                .filter(Boolean)
                .map((seg, i) => (_jsx("path", { d: 'M' + seg }, i)))] }));
}
//# sourceMappingURL=Icon.js.map